import { filter, ignoreElements, withLatestFrom, catchError, switchMap } from 'rxjs';
import { EMPTY, Observable, merge, of } from 'rxjs';
import { Action } from 'redux';

import {
    createStreamOfSWMessages,
    handleVersionUpdates,
    pollForServiceWorkerUpdates,
    registerKillSwitchServiceWorker,
    registerServiceWorker,
} from '../effects/serviceWorkers';
import { AppendState } from '../runtime.register';
import { assertUnreachable } from '../../utils/general';
import { EpicDeps } from '../../types';

export function serviceWorkerEpic(
    _action$: Observable<any>,
    _state$: Observable<AppendState>,
    deps: EpicDeps,
): Observable<Action> {
    return of(deps.env).pipe(
        withLatestFrom(deps.window$),
        /**
         * Only progress if service worker is supported
         */
        filter(([, window]) => 'serviceWorker' in window.navigator),
        /**
         * If we get here, service worker was enabled & supported by the browser
         */
        switchMap(([env]) => {
            switch (Boolean(env.SERVICE_WORKER)) {
                /**
                 * If the service worker is enabled, just go ahead
                 * and register as normal
                 */
                case true:
                    const sw$ = registerServiceWorker(
                        env.VERSION,
                        // the service worker has no access to the env, so it's told where the assets are
                        env.CDN_URL ? `/sw.js?cdn=${encodeURIComponent(env.CDN_URL)}` : undefined,
                    );
                    /**
                     * Message stream coming out of the service worker
                     */
                    const message$ = sw$.pipe(switchMap(createStreamOfSWMessages));
                    /**
                     * Message handlers, even though there's only 1 for now.
                     */
                    const msgHandlers$ = merge(handleVersionUpdates(message$, of(env.VERSION!)));
                    /**
                     * Poll for service worker update once the current SW is registered
                     */
                    const updates = sw$.pipe(
                        switchMap(pollForServiceWorkerUpdates(deps.historyEvents$)),
                        ignoreElements(),
                    );
                    return merge(msgHandlers$, updates);
                case false:
                    return registerKillSwitchServiceWorker();
                default:
                    return assertUnreachable();
            }
        }),
        catchError((e) => {
            // SW is an enhancement, so we're going to 'ignore' any error
            console.error('serviceWorkerEpic Error', e);
            return EMPTY;
        }),
    );
}
